import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiFetch } from '../apiFetch';

interface ShoppingModeState {
  isActive: boolean;
  storeId: number | null;
  storeName: string | null;
  listId: number | null;
  listName: string | null;
}

interface ShoppingModeContextType {
  shoppingMode: ShoppingModeState;
  startShopping: (storeId: number, storeName: string, listId: number, listName: string) => void;
  endShopping: () => void;
  pauseShopping: () => void;
  refreshActiveList: () => Promise<void>;
}

const defaultState: ShoppingModeState = {
  isActive: false,
  storeId: null,
  storeName: null,
  listId: null,
  listName: null,
};

const STORAGE_KEY = 'shoppingMode';

const ShoppingModeContext = createContext<ShoppingModeContextType | undefined>(undefined);

export const ShoppingModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [shoppingMode, setShoppingMode] = useState<ShoppingModeState>(defaultState);
  const apiUrl = process.env.REACT_APP_API_URL;

  // Fetch active list from backend
  const refreshActiveList = useCallback(async () => {
    const token = localStorage.getItem('access_token');
    if (!token) {
      setShoppingMode(defaultState);
      return;
    }

    try {
      const lists = await apiFetch(`${apiUrl}/api/shopping-lists/`);
      const activeList = lists.find((list: any) => list.status === 'active');

      if (activeList && activeList.shop) {
        setShoppingMode({
          isActive: true,
          storeId: activeList.shop,
          storeName: activeList.shop_name || null,
          listId: activeList.id,
          listName: activeList.name,
        });
      } else {
        setShoppingMode(defaultState);
      }
    } catch (err) {
      // On error, clear the state
      setShoppingMode(defaultState);
    }
  }, [apiUrl]);

  // Fetch active list on mount and when auth changes
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      refreshActiveList();
    }

    const handleAuthChange = () => {
      const newToken = localStorage.getItem('access_token');
      if (newToken) {
        refreshActiveList();
      } else {
        setShoppingMode(defaultState);
      }
    };

    window.addEventListener('authChange', handleAuthChange);
    return () => window.removeEventListener('authChange', handleAuthChange);
  }, [refreshActiveList]);

  // Also persist to localStorage for offline access / quick restore
  useEffect(() => {
    if (shoppingMode.isActive) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(shoppingMode));
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [shoppingMode]);

  const startShopping = useCallback((storeId: number, storeName: string, listId: number, listName: string) => {
    setShoppingMode({
      isActive: true,
      storeId,
      storeName,
      listId,
      listName,
    });
  }, []);

  const endShopping = useCallback(() => {
    setShoppingMode(defaultState);
  }, []);

  const pauseShopping = useCallback(() => {
    setShoppingMode(defaultState);
  }, []);

  return (
    <ShoppingModeContext.Provider value={{ shoppingMode, startShopping, endShopping, pauseShopping, refreshActiveList }}>
      {children}
    </ShoppingModeContext.Provider>
  );
};

export const useShoppingMode = (): ShoppingModeContextType => {
  const context = useContext(ShoppingModeContext);
  if (!context) {
    throw new Error('useShoppingMode must be used within a ShoppingModeProvider');
  }
  return context;
};
